/**
 * dirty snapshot + beforeunload + ثبت در NavigationGuard
 */
import { useCallback, useEffect, useRef } from "react";
import { useNavigationGuard } from "./useNavigationGuard";

function valuesEqual(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export function useUnsavedChangesGuard({
  getValues,
  snapshotRef, // mutable ref holding baseline
  enabled = true,
  canFinalize,
  onSaveDraft,
  onFinalize,
  onDiscard,
}) {
  const { register } = useNavigationGuard();
  const getValuesRef = useRef(getValues);
  getValuesRef.current = getValues;

  const isDirty = useCallback(() => {
    if (!enabled) return false;
    const snap = snapshotRef.current || {};
    const cur = getValuesRef.current() || {};
    const keys = new Set([...Object.keys(snap), ...Object.keys(cur)]);
    for (const k of keys) {
      if (!valuesEqual(cur[k], snap[k])) return true;
    }
    return false;
  }, [enabled, snapshotRef]);

  useEffect(() => {
    if (!enabled) return undefined;
    return register({
      isDirty,
      canFinalize: () => (typeof canFinalize === "function" ? canFinalize() : { ok: false, reason: "" }),
      onSaveDraft,
      onFinalize,
      onDiscard,
    });
  }, [enabled, register, isDirty, canFinalize, onSaveDraft, onFinalize, onDiscard]);

  useEffect(() => {
    if (!enabled) return undefined;
    const h = (e) => {
      if (!isDirty()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [enabled, isDirty]);

  return { isDirty };
}
